import React, { useRef, useEffect, useLayoutEffect, useState } from "react"
import styled, { css } from "styled-components"
import useAnimationFrame from "../hooks/useAnimationFrame";
import MemoChildren from "./MemoChildren";
import Scroll from "./ScrollNew";

const StickySlide = ({ children, stickyTop = undefined, sceneIndex = undefined, setSceneIndex = undefined, captionPosition = undefined, hasScrollPrompt = undefined }) => {
  const stepWrapRef = useRef();
  const stepRef = useRef();
  const [sceneIsActive, setSceneIsActive] = useState(false);

  useEffect(() => {
    sceneIsActive && sceneIndex !== undefined && setSceneIndex && setSceneIndex(sceneIndex);
  }, [sceneIsActive]);

  useEffect(() => {
    if ( ! stepWrapRef.current ) return;
    
    let observer;
    let options = {
      root: null,
      rootMargin: "0px",
      threshold: [0.2,0.25,0.3]
    };
    observer = new IntersectionObserver(( entries ) => {
      if ( entries[0].intersectionRatio > 0.25 ) {
        setSceneIsActive(true);
      }
      else {
        setSceneIsActive(false);
      }
    }, options);
    observer.observe(stepWrapRef.current);

    return () => observer.disconnect();
    
  }, []);

  return (
    <StyledStepItem ref={stepWrapRef} className={sceneIsActive ? 'is-active' : undefined}>
      <StickySlideContent
        stepRef={stepRef}
        stepWrapRef={stepWrapRef}
        captionPosition={captionPosition}
        stickyTop={stickyTop}
      >
        <MemoChildren debug="sticky slide caption children render">
          <div>{children}</div>
        </MemoChildren>
      </StickySlideContent>
    </StyledStepItem>
  );
};

export default StickySlide;


const StickySlideContent = ({ children, stepRef, stepWrapRef, captionPosition, stickyTop }) => {

  const [opacity, setOpacity] = useState(0);
  const [transform, setTransform] = useState(0);

  const animate = time => {
    if ( ! stepRef.current ) return;

    const duration = 100;
    const friction = 0.0625;

    const calcOffsetTop = stepRef.current.offsetTop;
    const stepContentHeight = stepRef.current.offsetHeight;
    const stepTopStyle = parseInt(window.getComputedStyle(stepRef.current).getPropertyValue('top'), 10);
    const stepHeight = stepWrapRef.current.offsetHeight;
    let updatedOpacity = 0;
    let updatedTransform = 0;

    if ( calcOffsetTop < stepTopStyle && calcOffsetTop < duration ) {
      updatedOpacity = 1 - ((-100 + ((calcOffsetTop) * (100 / duration))) / -100);
      
    }
    else if ( calcOffsetTop > 0 && (calcOffsetTop + duration + stepContentHeight < stepHeight ) ) {
      updatedOpacity = 1;
    }
    else if ( calcOffsetTop > 0 && (20 + calcOffsetTop + duration + stepContentHeight > stepHeight ) ) {
      updatedOpacity = ((-100 + (((20 + calcOffsetTop + duration + stepContentHeight) - stepHeight ) * (100 / duration))) / -100);
    }

    updatedTransform = (calcOffsetTop * friction).toFixed(2);

    setTransform(prevTransform => updatedTransform);
    setOpacity(prevOpacity => updatedOpacity.toFixed(2));
    
  }

  useAnimationFrame(deltaTime => animate());

  return (
    <>
      {stickyTop && (
        <StyledStepStickyTop style={{opacity: opacity}}>
          <MemoChildren debug="sticky slide top children">{stickyTop}</MemoChildren>
        </StyledStepStickyTop>
      )}
      <StyledStepCaption 
        ref={stepRef} 
        style={{
          "--opacity": opacity,
          "--translate-y": `-${transform}px`,
        }}
        captionPosition={captionPosition}
      >
          {children}
      </StyledStepCaption>
    </>
  )
}


const StyledStepStickyTop = styled.div`
  position: fixed;
  z-index: 1;
  top: 0;
  height: calc(50vh + calc(3 * var(--text-size)));
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  padding: 0 var(--gutter);
  display: none;
  .is-active > & {
    display: flex;
    z-index: 3;
  }
`

const StyledStepCaption = styled.div`
  position: sticky;
  top: 50%;
  opacity: var(--opacity);
  transform: translate3d(0,var(--translate-y),0);
  transition: transform var(--inertia) ease-out;
  will-change: transform;
  > div {
    position: relative;
    transform: translate3d(0,-50%,0);
  }
  &:not(:first-of-type) {
    top: calc(50% + 12em);
  }
  ${props => props.captionPosition === 'bottom-left' && css`
    align-self: flex-start;
    margin-left: var(--gutter);
    top: 80%;
    @media ( max-width: 48em ) {
      margin-left: var(--gutter);
      margin-right: var(--gutter);
      max-width: 100%;
    }
  `}
`

const StyledStepItem = styled.div`
  position: relative;
  z-index: 1;
  &.is-active {
    z-index: 3;
  }
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
`
